import { getGameResult } from "@cloudnest/augmented-chess-engine";
import { useEffect, useMemo, useState } from "react";
import { BoardThemed } from "../../chess-ui";
import { navigateTo } from "../../router";
import { CHESS_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import type { ChessReplaySnapshot } from "./replay-runner";
import { buildChessReplaySnapshots } from "./replay-runner";

interface ReplayScreenProps {
  readonly scenario: string;
}

function clampStepIndex(
  index: number,
  snapshots: readonly ChessReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly ChessReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);

  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly ChessReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function eventSummary(snapshot: ChessReplaySnapshot): string {
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}`;
}

function resultLabel(snapshot: ChessReplaySnapshot): string {
  const result = getGameResult(snapshot.state);
  if (!result.ended)
    return `${snapshot.state.phase} / ${snapshot.state.currentPlayer}`;
  return `${result.result ?? "ENDED"} / ${result.reason ?? "complete"}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = CHESS_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildChessReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">Replay not found</h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            Close
          </button>
        </section>
      </div>
    );
  }

  const maxIndex = snapshots.length - 1;

  return (
    <div
      className="min-h-screen bg-surface-primary"
      data-testid="chess-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="chess-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm text-text-primary"
              data-testid="chess-replay-event-summary"
            >
              {eventSummary(current)}
            </p>
            <p
              className="text-text-secondary text-xs"
              data-testid="chess-replay-status"
            >
              {resultLabel(current)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="chess-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => setReplayStep(safeStepIndex - 1)}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={safeStepIndex}
              className="w-36 md:w-56"
              data-testid="chess-replay-step-slider"
              onChange={(event) => setReplayStep(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="chess-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => setReplayStep(safeStepIndex + 1)}
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="chess-replay-step-count"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
        </div>
      </div>

      <main className="mx-auto flex max-w-5xl flex-col gap-4 p-4">
        <div
          className="rounded-md border border-border-primary bg-surface-secondary px-4 py-2 text-sm text-text-secondary"
          data-testid="observer-mode-banner"
        >
          Replay observer · read only
        </div>
        <div
          className="chess-board-container rounded-lg p-3"
          style={{
            background: "var(--cn-chess-board-frame)",
            border: "1px solid var(--cn-chess-board-frame)",
          }}
          data-testid="chess-replay-board-frame"
        >
          <BoardThemed
            gameState={current.state}
            selectedPieceId={null}
            legalMoves={[]}
            isMyTurn={false}
            gameEnded
            onSquareClick={() => undefined}
          />
        </div>
      </main>
    </div>
  );
}
